import { useId, type ReactNode } from "react";
import { copy } from "../copy";
import Icon from "../components/Icon";
import type { ContestFilters, FilterOptions } from "./filters";

type Props = {
  filters: ContestFilters;
  options: FilterOptions;
  resultCount: number;
  activeCount: number;
  member: boolean;
  onChange: <K extends keyof ContestFilters>(key: K, value: ContestFilters[K]) => void;
  onReset: () => void;
  onSearch: () => void;
};

const inputClass =
  "h-10 w-full min-w-0 rounded-md border border-ground-edge bg-ground px-3 text-sm text-chrome placeholder:text-chrome-dim transition-colors focus:border-gold/60 focus:outline-none";

function Field({ label, children }: { label: string; children: (id: string) => ReactNode }) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="mb-2 block text-[11px] font-bold uppercase tracking-[0.16em] text-chrome-dim">
        {label}
      </label>
      {children(id)}
    </div>
  );
}

function Select({
  id,
  value,
  all,
  options,
  onChange,
}: {
  id: string;
  value: string;
  all: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  return (
    <div className="relative">
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${inputClass} cursor-pointer appearance-none pr-9`}
      >
        <option value="" className="bg-ground text-chrome">
          {all}
        </option>
        {options.map((o) => (
          <option key={o} value={o} className="bg-ground text-chrome">
            {o}
          </option>
        ))}
      </select>
      <Icon name="arrowDown" className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-chrome-dim" />
    </div>
  );
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="bf-focusable group flex cursor-pointer items-center gap-2.5 py-0.5 text-left"
    >
      <span
        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border transition-colors ${
          checked ? "border-money bg-money text-ground" : "border-ground-edge bg-ground"
        }`}
      >
        {checked && <Icon name="check" className="h-3 w-3" />}
      </span>
      <span
        className={`text-[13px] font-medium transition-colors ${checked ? "text-chrome" : "text-chrome-dim group-hover:text-chrome"}`}
      >
        {label}
      </span>
    </button>
  );
}

// The filter rail. Every option list is derived from the contests the page
// loaded; My contests shows only for a signed-in viewer.
export default function FilterRail({
  filters,
  options,
  resultCount,
  activeCount,
  member,
  onChange,
  onReset,
  onSearch,
}: Props) {
  const c = copy.contests;
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSearch();
      }}
      aria-label={c.filtersLabel}
      className="rounded-2xl border border-ground-edge bg-ground-raised p-5"
    >
      <div className="flex items-baseline justify-between">
        <h2 className="font-headline text-2xl uppercase leading-none tracking-[-0.01em] text-chrome">{c.search}</h2>
        {activeCount > 0 && (
          <span className="bf-tabular rounded-full bg-gold/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-gold">
            {c.active(activeCount)}
          </span>
        )}
      </div>
      <p className="bf-tabular mt-2 text-[11px] uppercase tracking-[0.16em] text-chrome-dim">{c.found(resultCount)}</p>

      <div className="mt-6 space-y-5">
        <Field label={c.host}>
          {(id) => (
            <Select id={id} value={filters.host} all={c.allHosts} options={options.host} onChange={(v) => onChange("host", v)} />
          )}
        </Field>

        <Field label={c.prize}>
          {(id) => (
            <div className="flex items-center gap-2">
              <input
                id={id}
                type="number"
                inputMode="numeric"
                min={0}
                value={filters.prizeMin}
                onChange={(e) => onChange("prizeMin", e.target.value)}
                placeholder={c.min}
                aria-label={`${c.prize} ${c.min}`}
                className={inputClass}
              />
              <span aria-hidden className="text-chrome-dim">
                –
              </span>
              <input
                type="number"
                inputMode="numeric"
                min={0}
                value={filters.prizeMax}
                onChange={(e) => onChange("prizeMax", e.target.value)}
                placeholder={c.max}
                aria-label={`${c.prize} ${c.max}`}
                className={inputClass}
              />
            </div>
          )}
        </Field>

        <div className="space-y-3 border-y border-ground-edge py-4">
          <Toggle label={c.seatsOpen} checked={filters.seatsOpen} onChange={(v) => onChange("seatsOpen", v)} />
          {member && <Toggle label={c.myContests} checked={filters.myContests} onChange={(v) => onChange("myContests", v)} />}
          <Toggle label={c.versus} checked={filters.versus} onChange={(v) => onChange("versus", v)} />
          <Toggle label={c.freeAdmission} checked={filters.freeAdmission} onChange={(v) => onChange("freeAdmission", v)} />
        </div>

        <Field label={c.genre}>
          {(id) => (
            <Select id={id} value={filters.genre} all={c.allGenres} options={options.genre} onChange={(v) => onChange("genre", v)} />
          )}
        </Field>
        <Field label={c.challenge}>
          {(id) => (
            <Select
              id={id}
              value={filters.challenge}
              all={c.allChallenges}
              options={options.challenge}
              onChange={(v) => onChange("challenge", v)}
            />
          )}
        </Field>
        <Field label={c.duration}>
          {(id) => (
            <Select
              id={id}
              value={filters.duration}
              all={c.anyDuration}
              options={options.duration}
              onChange={(v) => onChange("duration", v)}
            />
          )}
        </Field>
      </div>

      <div className="mt-6 flex items-center gap-3">
        <button
          type="button"
          onClick={onReset}
          className="bf-focusable h-10 flex-1 whitespace-nowrap rounded-md border border-ground-edge text-[11px] font-bold uppercase tracking-[0.14em] text-chrome-dim transition-colors hover:border-chrome-dim/50 hover:text-chrome"
        >
          {c.reset}
        </button>
        <button
          type="submit"
          className="bf-focusable h-10 flex-1 whitespace-nowrap rounded-md bg-money text-[11px] font-bold uppercase tracking-[0.14em] text-ground transition-colors hover:bg-money-deep"
        >
          {c.search}
        </button>
      </div>
    </form>
  );
}
